/**
 * Single team card inside a round on the admin Results tab.
 *
 * Shows: placement / team name / points / deaths, the authoritative combat-log
 * player, full member list with each member's timezone, and every boss kill
 * with UTC + local-combat-log timestamps.
 */

import { formatDuration } from '@swtorbit/shared-react-hooks';
import { statusBadgeClass } from '@swtorbit/shared-components';
import {
  MEDALS,
  type AdminFprResultsTeamRound,
} from '@swtorbit/shared-socket-events';

interface Props {
  team: AdminFprResultsTeamRound;
}

function fmtUtc(ms: number | null): string {
  if (ms == null) return '—';
  return `${new Date(ms).toISOString().replace('T', ' ').slice(0, 19)} UTC`;
}

function fmtElapsed(ms: number | null | undefined): string {
  if (ms == null) return '—';
  return formatDuration(Math.round(ms / 1000));
}

export function FprResultsTeamCard({ team }: Props) {
  const placementBadge =
    team.status === 'dnf'
      ? <span className="badge badge--red">DNF</span>
      : team.placement >= 1 && team.placement <= 3
        ? <span className="fs-lg">{MEDALS[team.placement - 1]}</span>
        : team.placement > 3
          ? <span className="text-bold">{team.placement}.</span>
          : null;

  return (
    <div
      className={`bg-space bg-o-50 text-white border-md rounded-base p-sm d-flex flex-column gap-xs ${
        team.placement > 0 && team.placement <= 3 ? 'border-gold glow glow-gold' : 'border-blue-light'
      }`}
    >
      {/* Header row */}
      <div className="d-flex justify-content-between align-items-center gap-xs flex-wrap">
        <div className="d-flex align-items-center gap-xs">
          {placementBadge}
          <span className="text-bold fs-base">{team.teamName}</span>
          <span className={statusBadgeClass(team.status)}>{team.status.toUpperCase()}</span>
        </div>
        <div className="d-flex align-items-center gap-xs">
          {team.deathCount > 0 && <span className="text-cyan">💀 {team.deathCount}</span>}
          <span className="text-bold">{team.roundPoints} pts</span>
        </div>
      </div>

      {/* Race start */}
      <div className="d-flex flex-wrap gap-xs ff-display fs-sm">
        <span className="text-slate">Started:</span>
        <span className="text-cyan">{fmtUtc(team.raceStartedAtUtcMs)}</span>
        {team.entryTimestamp && (
          <span className="text-slate">(local {team.entryTimestamp})</span>
        )}
        {team.finishElapsedMs != null && (
          <>
            <span className="text-slate">· Finish:</span>
            <span className="text-bold">{fmtElapsed(team.finishElapsedMs)}</span>
          </>
        )}
      </div>

      {/* Members */}
      <div className="d-flex flex-column gap-2xs">
        <span className="text-slate fs-sm text-uppercase">Members</span>
        {team.members.length === 0 ? (
          <span className="text-cyan fs-sm">No members</span>
        ) : (
          team.members.map((m) => (
            <div
              key={m.playerId}
              className={`d-flex justify-content-between align-items-center gap-xs fs-sm ${m.isAuthoritative ? 'text-gold' : 'text-white'}`}
            >
              <div className="d-flex align-items-center gap-2xs flex-wrap">
                {m.isAuthoritative && <span title="Combat log authoritative for this round">📜</span>}
                {m.role === 'lead' && <span title="Team leader">👑</span>}
                <span>{m.characterName ?? 'Unknown character'}</span>
                {m.discordUsername && <span className="text-slate">· {m.discordUsername}</span>}
                {m.discipline ? (
                  <span className="text-slate ff-display">
                    · {m.combatStyle ? `${m.combatStyle} / ${m.discipline}` : m.discipline}
                  </span>
                ) : (
                  <span
                    className="text-amber ff-display"
                    title="No DisciplineChanged combat-log event was captured for this player in this round"
                  >
                    · Discipline not detected
                  </span>
                )}
              </div>
              <span className="text-slate ff-display">
                {m.timezone ?? 'Unknown TZ'}
              </span>
            </div>
          ))
        )}
      </div>

      {/* Boss kills */}
      <div className="d-flex flex-column gap-2xs">
        <span className="text-slate fs-sm text-uppercase">Boss Kills</span>
        {team.bossKills.length === 0 ? (
          <span className="text-cyan fs-sm">No kills yet</span>
        ) : (
          team.bossKills.map((k, idx) => (
            <div
              key={`${k.bossId}-${idx}`}
              className="d-flex justify-content-between align-items-center gap-xs fs-sm ff-display"
            >
              <span className="text-white">{k.bossName}</span>
              <div className="d-flex align-items-center gap-xs">
                <span className="text-cyan">+{fmtElapsed(k.elapsedMs)}</span>
                <span className="text-slate">{fmtUtc(k.killUtcMs)}</span>
                {k.killTimestampLocal && (
                  <span className="text-slate">(log {k.killTimestampLocal})</span>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
